import { useMemo } from 'react';
import { State } from '@weave/schema-gen-ts/dist/schemas/platform-location-feature/v1/platform-enums/location_feature_enum.pb';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { useGetAirSettingsQuery } from '@frontend/api-ai-settings';
import { useAppScopeStore, useScopedAppFlagStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';

const ENABLED_CF_STATES = [State.ACTIVE, State.PROMOTE];

const useLocationCustomizationFlags = (locationIds: string[]) => {
  const { getCustomizationFlag } = useAppFlagStore();
  const { isFlagStoreLoaded } = useScopedAppFlagStore();

  return useMemo(() => {
    const hasEnabledFlag = (featureEnum: Feature, locationId: string) => {
      const [flag] = getCustomizationFlag({ locationIds: [locationId], featureEnum });
      return !!flag?.state && ENABLED_CF_STATES.includes(flag.state);
    };

    const airCfEnabledLocationIds = locationIds.filter((locationId) =>
      [Feature.TRUELARK_AI_MESSAGING, Feature.TRUELARK_AI_VOICE].some((featureEnum) =>
        hasEnabledFlag(featureEnum, locationId)
      )
    );

    const onlineSchedulingEnabledLocationIds = locationIds.filter((locationId) =>
      hasEnabledFlag(Feature.ONLINE_SCHEDULING, locationId)
    );

    return { airCfEnabledLocationIds, onlineSchedulingEnabledLocationIds, isLoading: !isFlagStoreLoaded };
  }, [getCustomizationFlag, isFlagStoreLoaded, locationIds]);
};

const usePreferenceRequestAirAccess = () => {
  const { selectedLocationIds } = useAppScopeStore();

  const { airCfEnabledLocationIds, isLoading: isLoadingAirCfEnabledLocationIds } =
    useLocationCustomizationFlags(selectedLocationIds);

  const { data, isLoading: isLoadingSettingsData } = useGetAirSettingsQuery({
    request: { locationIds: selectedLocationIds },
    options: { enabled: !!selectedLocationIds.length },
  });

  const airScheduleLocationIds =
    data?.locationAirSettings
      ?.filter((location) => location?.airSettings?.scheduleKnowledge?.schedulingType === 'WEAVE_REQUEST_SCHEDULING')
      .map((location) => location.locationId)
      .filter((locationId): locationId is string => !!locationId) ?? [];

  const airEnabledLocationIds = airScheduleLocationIds.filter((locationId) =>
    airCfEnabledLocationIds.includes(locationId)
  );

  const isLoadingAirEnabledLocationIds = isLoadingAirCfEnabledLocationIds || isLoadingSettingsData;

  return {
    airEnabledLocationIds,
    isLoadingAirEnabledLocationIds,
  };
};

// Hook to check access to schedule request features - like TODO: Add features
export const useScheduleRequestFeaturesAccess = () => {
  const { selectedLocationIds } = useAppScopeStore();
  const { airEnabledLocationIds, isLoadingAirEnabledLocationIds } = usePreferenceRequestAirAccess();

  const { onlineSchedulingEnabledLocationIds, isLoading: isLoadingOnlineSchedulingCF } =
    useLocationCustomizationFlags(selectedLocationIds);

  const enabledLocationIds = Array.from(new Set([...onlineSchedulingEnabledLocationIds, ...airEnabledLocationIds]));

  const hasAccess = airEnabledLocationIds.length > 0 || onlineSchedulingEnabledLocationIds.length > 0;
  const isLoading = isLoadingAirEnabledLocationIds || isLoadingOnlineSchedulingCF;

  return {
    hasScheduleRequestFeaturesAccess: !isLoading && hasAccess,
    isLoading,
    scheduleRequestFeatureEnabledlocationIds: enabledLocationIds,
  };
};
